const normalizeNutrientStatus = (status) => {
  // Return null is status is falsy (e.g., undefined, null, empty string)
  if (!status) return null;

  const normalizedStatus = status.trim().toLowerCase();

  // Map all "low-tier" variations to a single "low" value
  // Covers: "low", "very low", "very very low"
  if (
    normalizedStatus === "very low" ||
    normalizedStatus === "very very low" ||
    normalizedStatus === "low"
  ) {
    return "low";
  }

  // "medium" maps directly to "medium"
  if (normalizedStatus === "medium") {
    return "medium";
  }

  // "high" maps directly to "high"
  if (normalizedStatus === "high") {
    return "high";
  }

  // If none of the recognized values match, return null (unknown/unsupported status)
  return null;
};

/**
 * Maize Recommendation Table:
 * ┌────────────┬───────┬────────┬──────┐
 * │  Nutrient  │  Low  │ Medium │ High │
 * ├────────────┼───────┼────────┼──────┤
 * │ Nitrogen   │  120  │   60   │  30  │
 * │ Phosphorus │   60  │   30   │   0  │
 * │ Potassium  │   60  │   30   │   0  │
 * └────────────┴───────┴────────┴──────┘
 */
export const getMaizeValues = ({ nitrogenStatus, phosphorusStatus, potassiumStatus }) => {
  // Declare variables to hold the final recommended amounts
  let nitrogenValue;
  let phosphorusValue;
  let potassiumValue;

  // Normalize all three incoming statuses to ensure consistent matching below
  const normalizedNitrogenStatus = normalizeNutrientStatus(nitrogenStatus);
  const normalizedPhosphorusStatus = normalizeNutrientStatus(phosphorusStatus);
  const normalizedPotassiumStatus = normalizeNutrientStatus(potassiumStatus);

  // Nitrogen value is assigned based on the normalized status
  if (normalizedNitrogenStatus === "low") nitrogenValue = 120;
  if (normalizedNitrogenStatus === "medium") nitrogenValue = 60;
  if (normalizedNitrogenStatus === "high") nitrogenValue = 30;

  // Phosphorus value is assigned based on the normalized status
  if (normalizedPhosphorusStatus === "low") phosphorusValue = 60;
  if (normalizedPhosphorusStatus === "medium") phosphorusValue = 30;
  if (normalizedPhosphorusStatus === "high") phosphorusValue = 0;

  // Potassium value is assigned based on the normalized status
  if (normalizedPotassiumStatus === "low") potassiumValue = 60;
  if (normalizedPotassiumStatus === "medium") potassiumValue = 30;
  if (normalizedPotassiumStatus === "high") potassiumValue = 0;

  // Return all three values
  return {
    nitrogen: nitrogenValue,
    phosphorus: phosphorusValue,
    potassium: potassiumValue,
  };
};

/**
 * Sorghum Recommendation Table:
 * ┌────────────┬───────┬────────┬──────┐
 * │  Nutrient  │  Low  │ Medium │ High │
 * ├────────────┼───────┼────────┼──────┤
 * │ Nitrogen   │   64  │   32   │  16  │
 * │ Phosphorus │   32  │   16   │   0  │
 * │ Potassium  │   30  │   15   │   0  │
 * └────────────┴───────┴────────┴──────┘
 */
export const getSorghumValues = ({ nitrogenStatus, phosphorusStatus, potassiumStatus }) => {
  // Declare variables to hold the final recommended amounts
  let nitrogenValue;
  let phosphorusValue;
  let potassiumValue;

  // Normalize all three incoming statuses for consistent comparison
  const normalizedNitrogenStatus = normalizeNutrientStatus(nitrogenStatus);
  const normalizedPhosphorusStatus = normalizeNutrientStatus(phosphorusStatus);
  const normalizedPotassiumStatus = normalizeNutrientStatus(potassiumStatus);

  // Nitrogen value is assigned based on the normalized status
  if (normalizedNitrogenStatus === "low") nitrogenValue = 64;
  if (normalizedNitrogenStatus === "medium") nitrogenValue = 32;
  if (normalizedNitrogenStatus === "high") nitrogenValue = 16;

  // Phosphorus value is assigned based on the normalized status
  if (normalizedPhosphorusStatus === "low") phosphorusValue = 32;
  if (normalizedPhosphorusStatus === "medium") phosphorusValue = 16;
  if (normalizedPhosphorusStatus === "high") phosphorusValue = 0; // No application needed

  // Potassium value is assigned based on the normalized status
  if (normalizedPotassiumStatus === "low") potassiumValue = 30;
  if (normalizedPotassiumStatus === "medium") potassiumValue = 15;
  if (normalizedPotassiumStatus === "high") potassiumValue = 0; // No application needed

  // Return all three values
  return {
    nitrogen: nitrogenValue,
    phosphorus: phosphorusValue,
    potassium: potassiumValue,
  };
};

/**
 * Cowpea and Soyabean Recommendation Table:
 * ┌────────────┬───────┬────────┬──────┐
 * │  Nutrient  │  Low  │ Medium │ High │
 * ├────────────┼───────┼────────┼──────┤
 * │ Nitrogen   │   20  │   10   │   0  │
 * │ Phosphorus │   40  │   20   │   0  │
 * │ Potassium  │   20  │   10   │   0  │
 * └────────────┴───────┴────────┴──────┘
 */
export const getCowpeaAndSoyabeanValues = ({
  nitrogenStatus,
  phosphorusStatus,
  potassiumStatus,
}) => {
  // Declare variables to hold the final recommended amounts
  let nitrogenValue;
  let phosphorusValue;
  let potassiumValue;

  // Normalize all three incoming statuses for consistent comparison
  const normalizedNitrogenStatus = normalizeNutrientStatus(nitrogenStatus);
  const normalizedPhosphorusStatus = normalizeNutrientStatus(phosphorusStatus);
  const normalizedPotassiumStatus = normalizeNutrientStatus(potassiumStatus);

  // Nitrogen value is assigned based on the normalized status
  if (normalizedNitrogenStatus === "low") nitrogenValue = 20;
  if (normalizedNitrogenStatus === "medium") nitrogenValue = 10;
  if (normalizedNitrogenStatus === "high") nitrogenValue = 0;

  // Phosphorus value is assigned based on the normalized status
  if (normalizedPhosphorusStatus === "low") phosphorusValue = 40;
  if (normalizedPhosphorusStatus === "medium") phosphorusValue = 20;
  if (normalizedPhosphorusStatus === "high") phosphorusValue = 0;

  // Potassium value is assigned based on the normalized status
  if (normalizedPotassiumStatus === "low") potassiumValue = 20;
  if (normalizedPotassiumStatus === "medium") potassiumValue = 10;
  if (normalizedPotassiumStatus === "high") potassiumValue = 0;

  // Return all three values
  return {
    nitrogen: nitrogenValue,
    phosphorus: phosphorusValue,
    potassium: potassiumValue,
  };
};
